import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  buildLbtSourceCatalog,
  getLbtItemTypeLabel,
} from '../../../data/licensingCredentialRecords';
import {
  LBT_SOURCE_ROUTE,
  formatLbtSourceListDate,
  sortLbtSourceItems,
} from '../../../data/licensingSourceWorkflow';
import { buildSampleFormInventory, buildLbtFormInventoryForms } from '../../../data/formInventoryConfig';
import {
  buildSavedTradeCategoryRecords,
  getTradeCategoryName,
} from '../../../data/complianceConfiguration';

const tableCols =
  'minmax(0, 0.75fr) minmax(0, 0.55fr) minmax(0, 1.15fr) minmax(0, 0.7fr) minmax(0, 1.35fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 0.95fr) minmax(48px, auto)';

const RecordIconCell = ({ row }) => {
  const hasImage = Boolean(row.iconUrl);
  return (
    <span
      className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-lightgray shrink-0 overflow-hidden"
      aria-hidden
    >
      {hasImage ? (
        <img src={row.iconUrl} alt="" className="w-full h-full object-contain" />
      ) : (
        <i className="fi fi-rs-picture text-base text-primaryblue flex items-center justify-center w-full h-full leading-none" />
      )}
    </span>
  );
};

const ComplianceItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const forms = useMemo(() => {
    const inventory = Array.isArray(location.state?.forms)
      ? location.state.forms
      : buildSampleFormInventory();
    return [...inventory, ...buildLbtFormInventoryForms()];
  }, [location.state?.forms]);
  const tradeCategories = useMemo(
    () => location.state?.tradeCategories ?? buildSavedTradeCategoryRecords(),
    [location.state?.tradeCategories],
  );

  const [records, setRecords] = useState(() =>
    buildLbtSourceCatalog(forms, tradeCategories),
  );
  const [saveSuccess, setSaveSuccess] = useState('');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.SINGLE);

  useEffect(() => {
    const incoming = location.state?.lbtSourceItems;
    const flash = location.state?.flashMessage;
    if (Array.isArray(incoming)) {
      setRecords(incoming);
    }
    if (flash) {
      setSaveSuccess(flash);
    }
    if (incoming || flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const displayedRecords = useMemo(() => sortLbtSourceItems(records), [records]);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRows,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(displayedRecords);

  const openItemEdit = (row) => {
    navigate(`${LBT_SOURCE_ROUTE}/${encodeURIComponent(row.id)}/edit`, {
      state: {
        lbtSourceItem: row,
        lbtSourceItems: records,
        forms,
        tradeCategories,
      },
    });
  };

  const openAdd = () => {
    navigate(`${LBT_SOURCE_ROUTE}/add`, {
      state: { lbtSourceItems: records, forms, tradeCategories },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Licences, Badges & Tickets</AdminPageTitle>
          <AdminHeaderAction onClick={openAdd}>Add New</AdminHeaderAction>
        </div>

        {saveSuccess ? (
          <AdminSupportingText className="text-freshgreen mb-4">{saveSuccess}</AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            {displayedRecords.length === 0 ? (
              <div className="py-10 px-4 text-center">
                <AdminPanelTitle className="text-offblack">No records found</AdminPanelTitle>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols }}
                >
                  {[
                    'ID',
                    'Icon',
                    'Name',
                    'Type',
                    'Description',
                    'Trade Category',
                    'Created On',
                    'Last Updated',
                    'Actions',
                  ].map((label) => (
                    <div
                      key={label}
                      className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                    >
                      <AdminTableHeader className="truncate">{label}</AdminTableHeader>
                    </div>
                  ))}
                </div>

                {paginatedRows.map((row) => {
                  const tradeCategoryLabel = getTradeCategoryName(
                    tradeCategories,
                    row.tradeCategoryId,
                  );
                  return (
                    <div
                      key={row.id}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2"
                      style={{ gridTemplateColumns: tableCols }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block" title={row.id}>
                          {row.id}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <RecordIconCell row={row} />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={row.itemName}>
                          {row.itemName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody
                          className="truncate block"
                          title={getLbtItemTypeLabel(row.itemType)}
                        >
                          {getLbtItemTypeLabel(row.itemType)}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={row.description}>
                          {row.description || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={tradeCategoryLabel}>
                          {tradeCategoryLabel}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta
                          className="truncate block"
                          title={formatLbtSourceListDate(row.createdOn)}
                        >
                          {formatLbtSourceListDate(row.createdOn)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta
                          className="truncate block"
                          title={formatLbtSourceListDate(row.lastUpdatedOn)}
                        >
                          {formatLbtSourceListDate(row.lastUpdatedOn)}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center justify-center shrink-0 overflow-visible">
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[row.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(row.id);
                            }}
                            aria-expanded={!!rowActionsOpen[row.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[row.id] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  openItemEdit(row);
                                }}
                              >
                                Edit
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {showPagination ? (
            <ConfigurationListPaginationFooter
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          ) : null}
        </DashboardMainContainer>
      </div>
    </AdminLayout>
  );
};

export default ComplianceItems;
